import { Trans, useTranslation } from "react-i18next";
import { Fragment, useEffect, useMemo, useState } from "react";
import { AlertCircle, Plus, Trash2, X } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import { useLocale } from "@/hooks/useLocale";
import type { ILopDetail } from "@/types/(company)/payrollRuns.interface";
import {
  useCreateDisputeMutation,
  type DisputeCategory,
  type DisputeLineType,
} from "@/services/(company)/payrollDisputesApi";

const buildCategoryOptions = (
  t: (key: string) => string,
): { value: DisputeCategory; label: string }[] => [
  { value: "WRONG_AMOUNT", label: t("ui.wrong_amount") },
  { value: "MISSING_EARNING", label: t("ui.missing_earning") },
  { value: "WRONG_DEDUCTION", label: t("ui.wrong_deduction") },
  { value: "MISSING_OVERTIME", label: t("ui.missing_overtime") },
  { value: "WRONG_LEAVE_DEDUCTION", label: t("ui.wrong_leave_deduction") },
  { value: "WRONG_STATUTORY", label: t("ui.wrong_statutory") },
  { value: "OTHER", label: t("ui.other") },
];

const buildLineTypeOptions = (
  t: (key: string) => string,
): { value: DisputeLineType; label: string }[] => [
  { value: "EARNING", label: t("ui.earning") },
  { value: "BENEFIT", label: t("ui.benefit") },
  { value: "DEDUCTION", label: t("ui.deduction") },
];

export interface PayslipLineSeed {
  lineId?: number | null;
  lineType: DisputeLineType;
  name: string;
  currentAmount: number;
  /** On the Loss of Pay row: the docked dates it can be disputed by (B44). */
  lopDetail?: ILopDetail | null;
}

interface IProps {
  open: boolean;
  payslipId: number;
  currencyCode: string;
  seedLines: PayslipLineSeed[];
  onClose: () => void;
  onSuccess?: () => void;
}

interface SeedRowState {
  selected: boolean;
  proposedAmount: number;
  note: string;
  /** Loss of Pay dates disputed; the amount follows from them (B44). */
  disputedDates: string[];
}

const hasLopDates = (s: PayslipLineSeed) =>
  Boolean(s.lopDetail && s.lopDetail.dates.length && s.lopDetail.days > 0);

/**
 * What Loss of Pay becomes without the disputed days, at the payslip's own
 * day rate — the same figure the server works out and stores.
 */
const lopWithout = (s: PayslipLineSeed, dates: string[]) => {
  if (!s.lopDetail || !(s.lopDetail.days > 0)) return s.currentAmount;
  const rate = s.currentAmount / s.lopDetail.days;
  const days = s.lopDetail.dates
    .filter((d) => dates.includes(d.date))
    .reduce((sum, d) => sum + d.days, 0);
  return Math.max(0, Math.round((s.currentAmount - days * rate) * 100) / 100);
};

interface NewLine {
  _ui_id: string;
  lineType: DisputeLineType;
  name: string;
  proposedAmount: number;
  note: string;
}

/** The API's own minimum (CreateDisputeDto.description, @MinLength(10)). */
const DESCRIPTION_MIN = 10;

const toNumber = (v: string | number | undefined | null): number => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const groupKey = (s: PayslipLineSeed) =>
  `${s.lineType}__${s.name}__${s.lineId ?? "null"}`;

export const RaiseDisputeDialog = ({
  open,
  payslipId,
  currencyCode,
  seedLines,
  onClose,
  onSuccess,
}: IProps) => {
  const { t } = useTranslation("my-space");
  const [category, setCategory] = useState<DisputeCategory>("WRONG_AMOUNT");
  const [description, setDescription] = useState("");
  const [seedState, setSeedState] = useState<Record<string, SeedRowState>>({});
  const [newLines, setNewLines] = useState<NewLine[]>([]);
  // Errors show once Submit has been tried (B43): before, only a toast said
  // why nothing happened, and testers missed it.
  const [submitted, setSubmitted] = useState(false);
  const [createDispute, { isLoading }] = useCreateDisputeMutation();

  // Reset state on open
  useEffect(() => {
    if (open) {
      const init: Record<string, SeedRowState> = {};
      seedLines.forEach((s) => {
        init[groupKey(s)] = {
          selected: false,
          proposedAmount: s.currentAmount,
          note: "",
          disputedDates: [],
        };
      });
      setSeedState(init);
      setNewLines([]);
      setDescription("");
      setCategory("WRONG_AMOUNT");
      setSubmitted(false);
    }
  }, [open, seedLines]);

  const earnings = useMemo(
    () => seedLines.filter((s) => s.lineType === "EARNING"),
    [seedLines],
  );
  const benefits = useMemo(
    () => seedLines.filter((s) => s.lineType === "BENEFIT"),
    [seedLines],
  );
  const deductions = useMemo(
    () => seedLines.filter((s) => s.lineType === "DEDUCTION"),
    [seedLines],
  );

  const updateSeed = (key: string, patch: Partial<SeedRowState>) =>
    setSeedState((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...patch },
    }));

  const addNewLine = () =>
    setNewLines((prev) => [
      ...prev,
      {
        _ui_id: `new-${Date.now()}-${prev.length}`,
        lineType: "EARNING",
        name: "",
        proposedAmount: 0,
        note: "",
      },
    ]);

  const removeNewLine = (uid: string) =>
    setNewLines((prev) => prev.filter((l) => l._ui_id !== uid));

  const updateNewLine = (uid: string, patch: Partial<NewLine>) =>
    setNewLines((prev) =>
      prev.map((l) => (l._ui_id === uid ? { ...l, ...patch } : l)),
    );

  const selectedSeeds = useMemo(
    () =>
      seedLines
        .map((s) => ({ seed: s, state: seedState[groupKey(s)] }))
        .filter((x) => x.state?.selected),
    [seedLines, seedState],
  );

  const totalDiff = useMemo(() => {
    const fromSeeds = selectedSeeds.reduce((sum, { seed, state }) => {
      const d = toNumber(state.proposedAmount) - toNumber(seed.currentAmount);
      return sum + (seed.lineType === "DEDUCTION" ? -d : d);
    }, 0);
    const fromNew = newLines.reduce((sum, l) => {
      const d = toNumber(l.proposedAmount);
      return sum + (l.lineType === "DEDUCTION" ? -d : d);
    }, 0);
    return fromSeeds + fromNew;
  }, [selectedSeeds, newLines]);

  const totalLineCount = selectedSeeds.length + newLines.length;

  const descriptionLength = description.trim().length;
  const descriptionError =
    descriptionLength < DESCRIPTION_MIN
      ? t("toasts.description_must_be_at_least_10_characters")
      : null;
  const linesError =
    totalLineCount === 0
      ? t("toasts.select_at_least_one_line_or_add_a_new_one")
      : null;

  const handleSubmit = async () => {
    setSubmitted(true);
    if (descriptionError || linesError) return;
    for (const nl of newLines) {
      if (!nl.name.trim()) {
        toast.error(t("toasts.every_new_line_needs_a_name"));
        return;
      }
    }
    // A line that changes nothing is refused by the server (B44).
    const unchanged = selectedSeeds.find(
      ({ seed, state }) =>
        !state.disputedDates.length &&
        Math.abs(toNumber(state.proposedAmount) - seed.currentAmount) < 0.005,
    );
    if (unchanged) {
      toast.error(
        hasLopDates(unchanged.seed)
          ? t("toasts.pick_the_days_you_dispute")
          : t("toasts.line_proposal_unchanged", { name: unchanged.seed.name }),
      );
      return;
    }

    const proposedLines = [
      ...selectedSeeds.map(({ seed, state }) => ({
        lineId: seed.lineId ?? null,
        lineType: seed.lineType,
        name: seed.name,
        proposedAmount: toNumber(state.proposedAmount),
        note: state.note.trim() || undefined,
        disputedDates: state.disputedDates.length
          ? state.disputedDates
          : undefined,
      })),
      ...newLines.map((l) => ({
        lineId: null,
        lineType: l.lineType,
        name: l.name.trim(),
        proposedAmount: toNumber(l.proposedAmount),
        note: l.note.trim() || undefined,
      })),
    ];

    try {
      await createDispute({
        payslipId,
        category,
        description: description.trim(),
        proposedLines,
      }).unwrap();
      toast.success(t("toasts.dispute_raised_successfully"));
      onClose();
      onSuccess?.();
    } catch (err: any) {
      const msg = err?.data?.message;
      toast.error(
        Array.isArray(msg)
          ? msg.join(", ")
          : (msg ?? t("toasts.failed_to_raise_dispute")),
      );
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-sm shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-orange-50 text-orange-600">
              <AlertCircle size={20} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                {t("ui.raise_dispute")}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {t(
                  "ui.select_the_lines_you_want_to_dispute_or_add_missing_ones",
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">
          {/* Category */}
          <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-700">
                {t("my-space:shared.category")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <Select
                value={category}
                onValueChange={(v) => setCategory(v as DisputeCategory)}
              >
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {buildCategoryOptions(t).map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-700">
                {t("my-space:labels.description")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t(
                  "labels.describe_what_s_wrong_with_this_payslip",
                )}
                rows={2}
                maxLength={2000}
                aria-invalid={submitted && !!descriptionError}
                aria-describedby="dispute-description-help"
                className={cn(
                  "text-sm px-3 py-2 border rounded-xs resize-none focus:outline-none focus:ring-2",
                  submitted && descriptionError
                    ? "border-danger focus:ring-danger/30"
                    : "border-gray-200 focus:ring-blue-200",
                )}
              />
              <span
                id="dispute-description-help"
                className={cn(
                  "text-[11px]",
                  submitted && descriptionError
                    ? "text-danger"
                    : "text-muted-foreground",
                )}
              >
                {submitted && descriptionError
                  ? descriptionError
                  : t("ui.description_min_counter", {
                      count: descriptionLength,
                      min: DESCRIPTION_MIN,
                    })}
              </span>
            </div>
          </div>

          {/* Existing lines grouped */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-700">
                {t("ui.existing_lines_on_your_payslip")}
              </span>
              {submitted && linesError && (
                <span role="alert" className="text-[11px] text-danger">
                  {linesError}
                </span>
              )}
              <span className="text-[11px] text-gray-500">
                <Trans
                  t={t}
                  i18nKey="my-space:ui.selected_lines_net_diff"
                  values={{
                    count: totalLineCount,
                    currency: currencyCode,
                    diff: `${totalDiff > 0 ? "+" : ""}${totalDiff.toFixed(2)}`,
                  }}
                  components={{
                    count: <strong className="text-gray-900" />,
                    diff: (
                      <span
                        className={cn(
                          "font-semibold",
                          totalDiff > 0
                            ? "text-success-100"
                            : totalDiff < 0
                              ? "text-red-500"
                              : "",
                        )}
                      />
                    ),
                  }}
                />
              </span>
            </div>

            <SeedGroup
              title={t("labels.earnings")}
              tone="success"
              rows={earnings}
              state={seedState}
              currencyCode={currencyCode}
              onChange={updateSeed}
            />
            <SeedGroup
              title={t("labels.benefits")}
              tone="info"
              rows={benefits}
              state={seedState}
              currencyCode={currencyCode}
              onChange={updateSeed}
            />
            <SeedGroup
              title={t("labels.deductions")}
              tone="danger"
              rows={deductions}
              state={seedState}
              currencyCode={currencyCode}
              onChange={updateSeed}
            />
          </div>

          {/* New lines */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-700">
                {t("my-space:ui.missing_items_add_new")}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addNewLine}
                className="gap-1 h-7 text-xs"
              >
                <Plus size={12} /> {t("ui.add_new_line")}
              </Button>
            </div>

            {newLines.length === 0 && (
              <div className="text-xs text-gray-400 bg-gray-50 border border-dashed border-gray-200 rounded-xs px-3 py-3 text-center">
                {t("ui.item_missing_from_your_payslip_add_it_here")}
              </div>
            )}

            {newLines.map((l) => (
              <div
                key={l._ui_id}
                className="border border-blue-200 bg-blue-50/30 rounded-xs p-3 grid grid-cols-12 gap-2"
              >
                <div className="col-span-3">
                  <label className="block text-[10px] text-gray-500 mb-0.5">
                    {t("my-space:shared.type")}
                  </label>
                  <Select
                    value={l.lineType}
                    onValueChange={(v) =>
                      updateNewLine(l._ui_id, {
                        lineType: v as DisputeLineType,
                      })
                    }
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {buildLineTypeOptions(t).map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-5">
                  <label className="block text-[10px] text-gray-500 mb-0.5">
                    {t("my-space:settings.name")}
                  </label>
                  <input
                    type="text"
                    value={l.name}
                    onChange={(e) =>
                      updateNewLine(l._ui_id, { name: e.target.value })
                    }
                    placeholder={t("my-space:ui.eg_housing_allowance")}
                    className="w-full h-8 text-xs px-2 border border-gray-200 rounded-xs focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white"
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-[10px] text-gray-500 mb-0.5">
                    {t("ui.proposed_amount")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    value={l.proposedAmount}
                    onChange={(e) =>
                      updateNewLine(l._ui_id, {
                        proposedAmount: toNumber(e.target.value),
                      })
                    }
                    className="w-full h-8 text-xs px-2 border border-gray-200 rounded-xs focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white"
                  />
                </div>
                <div className="col-span-1 flex items-end justify-end">
                  <button
                    type="button"
                    onClick={() => removeNewLine(l._ui_id)}
                    className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-red-500"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="col-span-12">
                  <input
                    type="text"
                    value={l.note}
                    onChange={(e) =>
                      updateNewLine(l._ui_id, { note: e.target.value })
                    }
                    placeholder={t("labels.reason_note_optional")}
                    maxLength={1000}
                    className="w-full h-8 text-xs px-2 border border-gray-200 rounded-xs focus:outline-none focus:ring-2 focus:ring-blue-200 bg-white"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t("ui.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? t("ui.submitting") : t("ui.submit_dispute")}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Group component
// ────────────────────────────────────────────────────────────────────────────
const SeedGroup = ({
  title,
  tone,
  rows,
  state,
  currencyCode,
  onChange,
}: {
  title: string;
  tone: "success" | "info" | "danger";
  rows: PayslipLineSeed[];
  state: Record<string, SeedRowState>;
  currencyCode: string;
  onChange: (key: string, patch: Partial<SeedRowState>) => void;
}) => {
  const { t } = useTranslation(["my-space", "company"]);
  const { formatDate, formatNumber } = useLocale();
  if (rows.length === 0) return null;

  const headerClass = {
    success: "text-success-100",
    info: "text-blue-600",
    danger: "text-red-600",
  }[tone];

  return (
    <div className="border border-gray-200 rounded-xs overflow-hidden">
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
        <span className={cn("text-xs font-semibold uppercase", headerClass)}>
          {title}
        </span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-500 bg-gray-50/50 border-b border-gray-100">
            <th className="w-8 px-2 py-1.5"></th>
            <th className="text-start px-2 py-1.5 font-medium">
              {t("settings.name")}
            </th>
            <th className="text-end px-2 py-1.5 font-medium w-28">
              {t("labels.current")}
            </th>
            <th className="text-end px-2 py-1.5 font-medium w-32">
              {t("ui.proposed")}
            </th>
            <th className="text-end px-2 py-1.5 font-medium w-24">
              {t("ui.diff")}
            </th>
            <th className="text-start px-2 py-1.5 font-medium">
              {t("ui.note")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const key = groupKey(s);
            const rowState = state[key] ?? {
              selected: false,
              proposedAmount: s.currentAmount,
              note: "",
              disputedDates: [],
            };
            const byDate = hasLopDates(s);
            const toggleDate = (date: string, on: boolean) => {
              const dates = on
                ? [...rowState.disputedDates, date]
                : rowState.disputedDates.filter((d) => d !== date);
              onChange(key, {
                disputedDates: dates,
                proposedAmount: lopWithout(s, dates),
              });
            };
            const diff =
              Number(rowState.proposedAmount) - Number(s.currentAmount);
            return (
              <Fragment key={key}>
                <tr
                  className={cn(
                    "border-t border-gray-100",
                    rowState.selected && "bg-orange-50/30",
                  )}
                >
                  <td className="px-2 py-2 align-top">
                    <input
                      type="checkbox"
                      checked={rowState.selected}
                      onChange={(e) =>
                        onChange(key, { selected: e.target.checked })
                      }
                      className="size-4 accent-orange-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-2 py-2 align-top font-medium text-gray-900">
                    {/* The server names Loss of Pay in English (B45). */}
                    {s.lopDetail ? t("company:payrolls.loss_of_pay") : s.name}
                  </td>
                  <td className="px-2 py-2 align-top text-end text-gray-700">
                    {currencyCode} {s.currentAmount.toFixed(2)}
                  </td>
                  <td className="px-2 py-2 align-top">
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      // By date, the amount follows from the days ticked.
                      disabled={!rowState.selected || byDate}
                      value={rowState.proposedAmount}
                      onChange={(e) =>
                        onChange(key, {
                          proposedAmount: Number(e.target.value) || 0,
                        })
                      }
                      className="w-full h-7 text-xs px-1.5 border border-gray-200 rounded-xs focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100 disabled:text-gray-400 text-end bg-white"
                    />
                  </td>
                  <td
                    className={cn(
                      "px-2 py-2 align-top text-end font-semibold",
                      diff > 0
                        ? "text-success-100"
                        : diff < 0
                          ? "text-red-500"
                          : "text-gray-400",
                    )}
                  >
                    {rowState.selected
                      ? `${diff > 0 ? "+" : ""}${diff.toFixed(2)}`
                      : "—"}
                  </td>
                  <td className="px-2 py-2 align-top">
                    <input
                      type="text"
                      disabled={!rowState.selected}
                      value={rowState.note}
                      onChange={(e) => onChange(key, { note: e.target.value })}
                      placeholder={t("labels.reason_optional")}
                      className="w-full h-7 text-xs px-1.5 border border-gray-200 rounded-xs focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100 bg-white"
                    />
                  </td>
                </tr>
                {byDate && rowState.selected && (
                  <tr className="bg-orange-50/30">
                    <td />
                    <td colSpan={5} className="px-2 pb-3">
                      <p className="mb-1.5 text-[11px] text-gray-600">
                        {t("ui.lop_pick_days_hint", {
                          rate: formatNumber(
                            s.currentAmount / s.lopDetail!.days,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          ),
                          currency: currencyCode,
                        })}
                      </p>
                      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
                        {s.lopDetail!.dates.map((d) => {
                          const [y, m, day] = d.date.split("-").map(Number);
                          return (
                            <label
                              key={d.date}
                              className="flex cursor-pointer items-center gap-2 rounded-xs border border-gray-200 bg-white px-2 py-1.5 text-[11px]"
                            >
                              <input
                                type="checkbox"
                                checked={rowState.disputedDates.includes(
                                  d.date,
                                )}
                                onChange={(e) =>
                                  toggleDate(d.date, e.target.checked)
                                }
                                className="size-3.5 accent-orange-500"
                              />
                              <span className="font-medium text-gray-900">
                                {formatDate(
                                  new Date(y, m - 1, day),
                                  "EEE d MMM",
                                )}
                              </span>
                              <span className="text-gray-500">
                                {t(`ui.lop_reason_${d.reason.toLowerCase()}`)}
                                {d.days < 1 ? ` · ${t("ui.half_day")}` : ""}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
